import * as PIXI from 'pixi.js'
import Vec2 from './vec2.js'
import Z from './z.js'
import Bubble from './bubble.js'
import SheepBubble from './sheep-bubble.js'

class Player {
  constructor (game) {
    this.game = game

    this.jumpCount = 0
    this.hitsSheepCount = 0

    this.textures = {
      bubbleMan: PIXI.Texture.from('bubbleMan'),
      playerJumping: PIXI.Texture.from('playerJumping'),
      playerSuperJumping: PIXI.Texture.from('playerSuperJumping'),
      jump1: PIXI.Texture.from('jump1'),
      jump2: PIXI.Texture.from('jump2'),
      jump3: PIXI.Texture.from('jump3'),
      jump4: PIXI.Texture.from('jump4'),
      jump5: PIXI.Texture.from('jump5'),
      jump6: PIXI.Texture.from('jump6')
    }
    this.setTextures([this.textures.playerJumping])

    this.audioAliases = [
      'audioSheep1',
      'audioSheep2',
      'audioSheep3',
      'audioSheep4',
      'audioSheep5',
      'audioSheep6',
      'audioSheep7',
      'audioSheep8'
    ]

    this.friction = 2
    this.speed = 4
    this.jumpSpeed = 0
    this.direction = 'right'

    this.vGamma = null

    // 移動速率倍率
    this.speedBase = 1.2

    // 彈跳力
    this.bounce = 1

    // 是否正在進行超級跳躍
    this.isSuperJumping = false

    // 是否正在進行普通跳躍
    this.isJumping = false

    // 是否在畫面上凍結
    this.isFreeze = false

    // 是否無敵狀態
    this.isUnbeatable = !!this.game.debugMode

    this.isGeneratesBubbles = true

    // 睡覺形象 Z 符號的容器
    this.zs = []

    this.bubbles = []

    this.jumpBubblesCount = 20

    // 普通跳躍蹦出的 Z 數量
    this.jumpZCount = 2

    // 飛行蹦出的 Z 數量
    this.flyZCount = 6

    // super jump 的 Z 數量
    this.superJumpZCount = 8

    // 睡覺形象泡泡物件
    this.bubble = new Bubble(this.game)

    // 給 Z 用的控制變數，但為了讓 dat.GUI 讀到所以把他移到這裡了 QQ
    this.controls = {
      ay: 0.4, // -2 ~ 2
      fade: 0.96, // 0 ~ 1
      v: 10 // 0 ~ 50, step 0.1
    }

    if (this.game.debugMode) {
      this.folder = this.game.gui.addFolder('玩家')
      this.folder.add(this, 'isUnbeatable').name('無敵不會死')
      this.folder.add(this, 'isGeneratesBubbles').name('踩羊冒泡')
      this.folder.add(this, 'speed', 2, 10).name('左右移動速度').step(1)
      this.folder.add(this, 'speedBase', 0.1, 4).name('移動速率倍率').step(0.1)
      this.folder.add(this, 'bounce', 0.1, 5).name('彈跳力').step(0.1)
      this.folder.add(this, 'friction', 0.1, 3).name('重力係數').step(0.1)
      this.folder.add(this, 'jumpZCount', 2, 100).name('一般跳躍 Z 數').step(1)
      this.folder.add(this, 'flyZCount', 2, 100).name('記憶墊跳躍 Z 數').step(1)
      this.folder.add(this, 'superJumpZCount', 2, 100).name('彈簧跳躍 Z 數').step(1)
      this.folder.open()

      this.zFolder = this.game.gui.addFolder('Z 重力設定')
      this.zFolder.add(this.controls, 'ay', -2, 2).name('重力值').step(0.2)
      this.zFolder.add(this.controls, 'v', 0, 50).name('擴散度').step(0.1)
      this.zFolder.open()
    }
  }

  isFaceToRight () {
    return this.direction === 'right'
  }

  isFaceToLeft () {
    return this.direction === 'left'
  }

  faceRight () {
    this.direction = 'right'

    if (this.view.scale.x <= 0) {
      this.view.scale.x *= -1
    }
  }

  faceLeft () {
    this.direction = 'left'

    if (this.view.scale.x >= 0) {
      this.view.scale.x *= -1
    }
  }

  getJumpVelocity () {
    return (this.jumpSpeed / this.friction) * this.bounce
  }

  setPosition (x, y) {
    this.view.x = x
    this.view.y = y
  }

  setTextures (sprites) {
    if (this.view) {
      this.view.textures = sprites
    } else {
      this.view = new PIXI.extras.AnimatedSprite(sprites)
    }
    this.view.anchor.set(0.5, 0.5)
    this.view.scale.set(0.25)
    this.fixDirection()
  }

  freeze () {
    this.isFreeze = true
  }

  unfreeze () {
    this.isFreeze = false
  }

  jump () {
    if (this.isJumping) {
      return
    }

    if (this.game.audioEnabled) {
      if (this.game.scenesManager.scene3.nyanMode) {
        const alias = this.audioAliases[Math.floor(Math.random() * this.audioAliases.length)]
        PIXI.sound.play(alias)
      } else {
        PIXI.sound.play('audioJump')
      }
    }

    if (this.game.scenesManager.scene3.nyanMode && this.isGeneratesBubbles) {
      this.hitsSheepCount++
      this.generateBubbles(this.jumpBubblesCount)
    } else {
      this.generateZ(this.jumpZCount)
    }

    // 跳起來的樣子
    this.setTextures([
      this.textures.jump1,
      this.textures.jump2,
      this.textures.jump3
    ])
    this.view.animationSpeed = 0.2
    this.view.loop = false
    this.view.play()

    this.isJumping = true
    this.isFalling = false
    this.jumpSpeed = 20

    this.jumpCount++
  }

  superJump () {
    if (this.game.audioEnabled) {
      PIXI.sound.play('audioSuperJump')
    }
    this.generateZ(this.superJumpZCount)

    this.isSuperJumping = true
    this.isJumping = true
    this.isFalling = false
    this.jumpSpeed = 80
    this.controls.v = 4
    this.controls.ay = 0.8

    this.setTextures([this.textures.playerSuperJumping])
  }

  // 打嗑睡泡泡
  fly () {
    if (this.game.audioEnabled) {
      PIXI.sound.play('audioFly')
    }

    this.bubble.zoomOut()
    this.setTextures([this.textures.bubbleMan])
    this.generateZ(this.flyZCount)

    this.isJumping = true
    this.isFalling = false
    this.jumpSpeed = 40
  }

  jumping (onJumping, deltaTime) {
    if (onJumping) {
      onJumping()
    }

    if (!this.isJumping) {
      return
    }

    if (this.isSuperJumping && Math.random() > 0.65) {
      this.generateZ(this.superJumpZCount)
    }

    if (!this.isFreeze) {
      this.setPosition(this.view.x, this.view.y - (this.getJumpVelocity() * deltaTime * this.speedBase))
    }

    this.jumpSpeed -= ((this.friction / 4) * deltaTime * this.speedBase)

    if (this.jumpSpeed <= 0) {
      this.fall()
    }
  }

  fall () {
    this.isSuperJumping = false
    this.isFalling = true
    this.isJumping = false
    this.fallSpeed = 0
    this.controls.v = 10
    this.controls.ay = 0.4

    if (this.bubble.any) {
      this.bubble.zoomIn()
    }

    // 掉落的樣子
    this.setTextures([
      this.textures.jump4,
      this.textures.jump5,
      this.textures.jump6
    ])

    this.view.animationSpeed = 0.2
    this.view.loop = false
    this.view.play()
  }

  falling (onFalling, deltaTime) {
    if (onFalling) {
      onFalling()
    }

    if (!this.isFalling) {
      return
    }

    this.setPosition(this.view.x, this.view.y + this.fallSpeed)

    if (this.fallSpeed < 30) {
      this.fallSpeed += (1 * deltaTime)
    }
  }

  moveRight (deltaTime) {
    if (!this.isFaceToRight()) {
      this.faceRight()
    }

    const speed = this.vGamma ? ~~Math.abs(this.vGamma / 4) : this.speed
    this.view.x += (speed * deltaTime)
  }

  moveLeft (deltaTime) {
    if (!this.isFaceToLeft()) {
      this.faceLeft()
    }

    const speed = this.vGamma ? ~~Math.abs(this.vGamma / 4) : this.speed
    this.view.x -= (speed * deltaTime)
  }

  fixDirection () {
    if (this.isFaceToRight()) {
      this.faceRight()
    } else {
      this.faceLeft()
    }
  }

  generateZ (n = 3) {
    const v = this.controls.v

    for (var i = 0; i < n; i++) {
      const z = new Z(this.game, {
        p: new Vec2(this.view.x, this.view.y + 75),
        v: new Vec2(Math.random() * v - v / 2, Math.random() * v - v / 2),
        r: Math.random(),
        controls: this.controls
      })
      z.draw()
      this.zs.push(z)
    }
  }

  generateBubbles (n = 3) {
    const controls = {
      ay: -0.25, // -2 ~ 2
      fade: 0.96, // 0 ~ 1
      v: 10 // 0 ~ 50, step 0.1
    }

    for (var i = 0; i < n; i++) {
      const bubble = new SheepBubble(this.game, {
        p: new Vec2(this.view.x, this.view.y + 75),
        v: new Vec2(Math.random() * controls.v - controls.v / 2, Math.random() * controls.v - controls.v / 2),
        r: Math.random(),
        controls
      })
      bubble.draw()
      this.bubbles.push(bubble)
    }
  }

  isCollision (target) {
    let offsetA, offsetB
    if (this.isFaceToRight()) {
      offsetA = 35
      offsetB = 44
    } else {
      offsetA = 44
      offsetB = 35
    }

    return (this.view.x - (this.view.width / 2) + offsetA < target.view.x + (target.view.width / 2)) &&
      (this.view.x - (this.view.width / 2) + this.view.width - offsetB > (target.view.x - (target.view.width / 2))) &&
      (this.view.y - (this.view.height / 2) + this.view.height > target.view.y - (target.view.height / 2)) &&
      (this.view.y - (this.view.height / 2) + this.view.height < target.view.y + (target.view.height / 2) + this.fallSpeed)
  }

  destroy () {
    this.jumpCount = 0
    this.hitsSheepCount = 0

    if (this.zs.length > 0) {
      this.zs.forEach(z => z.destroy())
    }

    this.game.app.stage.removeChild(this.view)
    this.view.destroy()

    if (this.game.gui) {
      this.game.gui.removeFolder(this.folder)
      this.game.gui.removeFolder(this.zFolder)
    }
  }

  updateZs (deltaTime) {
    if (this.zs.length === 0) {
      return
    }

    this.zs.forEach((z, index) => {
      if (z.isCompleted) {
        this.zs.splice(index, 1)
        z.destroy()
        return
      } else {
        z.update(deltaTime)
        z.dropDown()
      }
    })
  }

  updateBubbles (deltaTime) {
    if (this.bubbles.length === 0) {
      return
    }

    this.bubbles.forEach((bubble, index) => {
      if (bubble.isCompleted) {
        this.bubbles.splice(index, 1)
        bubble.destroy()
        return
      } else {
        bubble.update(deltaTime)
        bubble.rise()
      }
    })
  }
}

export default Player
